"use strict";

const _ = require('lodash');
const d3 = require('d3');
const format = require('../lib/format');

function getKey(d) {
	return d._key;
}

function getValue(d) {
	return format(d.value);
}

class BasicLegend {
	constructor(options) {
		this._options = Object.assign({
			css: 'cm-legend', padding: 5, margin: 5,
			key: getKey, value: getValue
		}, options);
		_.bindAll(this, 'setKey');
	}

	get options() {
		return this._options;
	}

	get node() {
		return this._node;
	}

	select() {
		return d3.select(this._node);
	}

	get content() {
		return this._content;
	}

	get anchor() {
		return this._anchor;
	}

	get fill() {
		return this._fill;
	}

	get legend() {
		return this._legend;
	}

	getOrCall(prop, d, i) {
		if (_.isFunction(prop)) {
			return prop(d, i);
		}
		let p = _.get(d, prop);
		if (_.isFunction(p)) {
			return p(d, i);
		}
		return p;
	}

	get keyGetter() {
		return _.flow(_.partial(this.getOrCall, this.options.key), format);
	}

	get valueGetter() {
		return _.partial(this.getOrCall, this.options.value);
	}

	setKey(selection) {
		selection.exit().remove();
		selection.enter()
			.append('tspan').attr('dy', '1.2em').attr('x', 0)
			.classed('cm-key', true)
			.merge(selection).html(this.keyGetter);
	}

	setValue(selection) {
		selection.exit().remove();
		selection.enter()
			.append('tspan').attr('dy', '1.2em').attr('x', 0)
			.classed('cm-value', true)
			.merge(selection).html(this.valueGetter);
	}

	setContent({data}) {
		if (!data) {
			this.clearContent();
			return false;
		}
		this.content.selectAll('tspan.cm-key').data([data]).call(this.setKey);
		this.content.selectAll('tspan.cm-value').data([data]).call(this.setValue);
		return false;
	}

	clearContent() {
		this.content.text(null);
	}

	setFill() {
		let {width, height} = this.content.node().getBoundingClientRect();
		let padding = this.options.padding * 2;
		this.fill.attr('width', width + padding).attr('height', height + padding);
	}

	drawLegend() {
		var l = this._legend = this.select().append('g').style('visibility', 'hidden');
		this._fill = l.append('rect').classed('cm-fill', true);
		let padding = this.options.padding;
		this._content = l.append('text')
			.classed('cm-content', true).attr('transform', `translate(${padding},${padding})`);
	}

	prepareOptions(options) {
		options = Object.assign({}, this.options, options);
		this._options = options;
		this._node = options.node;
		return options;
	}

	draw(options) {
		options = this.prepareOptions(options);
		var s = this.select();
		s.attr('class', options.css);
		// We need to fix anchor
		this._anchor = s.append('rect').attr('width', 1).attr('height', 1);
		this.drawLegend();
	}

	position(options = {}) {
		let {width, height, margin, padding} = this.options;

		// Redraw content, if it returns false - do nothing
		if (!this.setContent(options)) {
			return;
		}

		// Update fill
		this.setFill();

		// Get legend size with fill and content.
		let {width: legendWidth, height: legendHeight} = this.legend.node().getBoundingClientRect();

		let canvasNode = this.node;
		let canvasRect = canvasNode.getBoundingClientRect();
		let [mouseX, mouseY] = d3.mouse(canvasNode);

		// Define x position
		let x;
		let node = options.node;
		if (node) {
			let nodeRect = node.getBoundingClientRect();
			let nodeX = nodeRect.left - canvasRect.left;
			let nodeWidth = nodeRect.width;
			if (width) {
				let space = width - (nodeX + legendWidth);
				if (space > legendWidth) {
					// To the right
					x = nodeX + nodeWidth + margin;
				} else {
					// To the left
					x = nodeX - legendWidth - margin;
				}
			} else {
				// To the right
				x = nodeX + nodeWidth + margin;
			}
		} else {
			x = mouseX;
			if (width) {
				let space = width - mouseX;
				if (space < legendWidth) {
					x -= legendWidth + margin;
				}
			}
		}

		// Define y position
		let y = mouseY;
		if (height) {
			y = Math.min(y, height - (legendHeight + margin));
		}
		this.legend.attr('transform', `translate(${x},${y})`);
	}

	show(options={}) {
		this.position(options);
		this.legend.style('visibility', 'visible');
	}

	hide(options={}) {
		this.legend.style('visibility', 'hidden');
	}
}
module.exports = BasicLegend;

